import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  ErrorModel,
  ResponseModel,
  UserImageModel,
  UserImageResponseModel,
  UserService as ApiUserService
} from '@core/api';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { FormService } from '@core/services';
import { ToastService } from '@ui/toast';
import { UserService } from '@core/auth';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { merge, Observable, of } from 'rxjs';
import { HttpStatusCode } from '@core/enums/http-status-code';
import { throwError } from 'rxjs/internal/observable/throwError';
import { TransportModel } from '@core/api/model/transportModel';

interface Data {
  transport: TransportModel;
  userId: number;
}

@Component({
  selector: 'user-modal-add-traction-car',
  templateUrl: './modal-add-traction-car.component.html',
  styleUrls: ['./modal-add-traction-car.component.scss']
})
export class ModalAddTractionCarComponent implements OnInit {

  form: FormGroup;
  transport: TransportModel;
  userId: number;
  loading = false;

  allowedPhotoTypes = [
    {
      type: UserImageModel.TypeEnum.TransportPassportFront,
      label: this.i18n('Фото оригинала свидетельства о госрегистрации ТС')
    }
  ];

  transportImages: Map<UserImageModel.TypeEnum, File | UserImageResponseModel> = new Map();

  imageForm: FormGroup = this.formBuilder.group({
    [UserImageModel.TypeEnum.TransportPassportFront]: [null]
  });

  constructor(
    public dialogRef: MatDialogRef<ModalAddTractionCarComponent>,
    private formBuilder: FormBuilder,
    private formService: FormService,
    private toastService: ToastService,
    private userService: UserService,
    private apiUserService: ApiUserService,
    private i18n: I18n,
    @Inject(MAT_DIALOG_DATA) public data: Data
  ) {

    this.transport = data && data.transport;
    this.userId = data && data.userId;

    if (this.transport && this.transport.image) {
      this.transportImages.set(this.transport.image.type, this.transport.image);
    }

    this.form = this.formBuilder.group({
      brand: [data && data.transport && data.transport.brand, [Validators.required]],
      model: [data && data.transport && data.transport.model, [Validators.required]],
      number: [data && data.transport && data.transport.number, [Validators.required]],
      state_number: [data && data.transport && data.transport.state_number, [Validators.required]]
    });
  }

  get editMod(): boolean {
    return !!(this.data && this.data.transport);
  }

  private get invalidSelectAllPhoto() {
    return !this.allowedPhotoTypes.every(item => !!this.transportImages.get(item.type));
  }

  selectTransportFile({ image, type }) {
    this.imageForm.get(type).setValue(image);
    this.transportImages.set(type, image);
  }

  submit() {
    this.loading = true;

    this.formService.markFormGroupTouched(this.form);

    if (this.form.invalid) {
      return this.loading = false;
    }

    if (this.transport) {
      this.requestUpdateTransportData();
    } else {
      this.requestCreateTransportData();
    }
  }

  requestUpdateTransportData() {
    return this.apiUserService.userIdTransportPut(this.userId, this.transport.id, this.form.value)
      .pipe(switchMap((res) => merge(...this.requestsUpdateTransportPhoto(res.data.id))))
      .pipe(finalize(() => this.loading = false))
      .subscribe(res => {
        this.dialogRef.close(true);
      }, error => {
        this.formService.serverValidateForm(this.form, error);
      });
  }

  requestCreateTransportData() {
    return this.apiUserService.userIdTransportPost(this.userId, this.form.value)
      .pipe(switchMap((res) => {
        this.transport = res.data;
        return merge(...this.requestsUpdateTransportPhoto(res.data.id))
      }))
      .pipe(finalize(() => this.loading = false))
      .subscribe(() => {
        this.dialogRef.close(this.transport);
      }, error => {
        this.formService.serverValidateForm(this.form, error);
      });
  }

  requestsUpdateTransportPhoto(transportId: number) {
    const requests: Array<Observable<ResponseModel<any>>> = [];

    this.transportImages.forEach((image, type) => {
      if (image instanceof File) {
        requests.push(this.apiUserService.userIdTransportIdImagePost(this.userId, transportId, {
            image,
            type
          }).pipe(catchError(err => {
            if (err.status === HttpStatusCode.UNPROCESSABLE_ENTITY) {
              err.errors[type] = err.errors && err.errors.image;

              this.formService.serverValidateForm(this.imageForm, err);
            }

            return throwError(err);
          }))
        );
      } else {
        requests.push(of({
          success: true,
          data: image
        }));
      }
    });

    if(!requests.length) {
      requests.push(of({
        success: true,
        data: null
      }));
    }

    return requests;
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  ngOnInit() {
  }

}
